/**
 * This slice is to manage validation types
 */
import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { LoadingStatus, SavingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { configValueService } from 'features/services/configValue.service';
import { v4 as uuidv4 } from 'uuid';

const initialState = {
    error: null,
    appConfigValues: [],
    editConfigValues: [],
    appLoadedClientId: null,
    appLoadedAssetId: null,
    editLoadedClientId: null,
    editLoadedAssetId: null,
    selectedClientId: 0,
    selectedAssetId: 0,
    appLoadingStatus: LoadingStatus.Idle,
    editLoadingStatus: LoadingStatus.Idle,
    savingStatus: SavingStatus.Idle,
};

export const createNewConfigValue = () => {
    return {
        config_value_id: uuidv4(),
        value: '',
        category: '',
        display_name: '',
        required: false,
        client_visible: false,
        client_obscured: false,
        valid_until_date: null,
        override_values: [],
    };
};

export const fetchAppConfigValues = createAsyncThunk(
    'configValues/fetchAppConfigValues',
    async ({ clientId, assetId }) => {
        return await configValueService.getAll(clientId, assetId);
    }
);

export const fetchEditConfigValues = createAsyncThunk(
    'configValues/fetchEditConfigValues',
    async ({ clientId, assetId }) => {
        return await configValueService.getAll(clientId, assetId);
    }
);

export const updateConfigValues = createAsyncThunk(
    'configValues/updateConfigValues',
    async (params, { rejectWithValue }) => {
        if (params.client_id !== 0) {
            // Set client id on each value
            params.config_values.map((value) => (value.client_id = params.client_id));
            params.editing_global = false;
            if (params.asset_id !== 0) {
                // Set asset id on each value
                params.config_values.map((value) => (value.asset_id = params.asset_id));
            } else {
                delete params?.asset_id;
            }
        } else {
            delete params?.client_id;
            delete params?.asset_id;
            params.editing_global = true;
        }
        const response = await configValueService.updateAll(params);
        if (!response) {
            return rejectWithValue(response);
        }
        return response;
    }
);

const configValuesSlice = createSlice({
    name: 'configValues',
    initialState,
    reducers: {
        clearAllConfigValues: (state) => {
            return initialState;
        },
        clearAppConfigValues: (state) => {
            state.appConfigValues = [];
            state.appLoadedClientId = null;
            state.appLoadedAssetId = null;
        },
        clearEditConfigValues: (state) => {
            state.editConfigValues = [];
            state.editLoadedClientId = null;
            state.editLoadedAssetId = null;
        },
        setSelectedClientId: (state, action) => {
            state.selectedClientId = action.payload;
            state.selectedAssetId = 0;
        },
        setSelectedAssetId: (state, action) => {
            state.error = null;
            state.editConfigValues = [];
            state.selectedAssetId = action.payload.assetId;
            state.editLoadedClientId = null;
            state.editLoadedAssetId = null;
            state.editLoadingStatus = LoadingStatus.Idle;
        },
        setSelectedClientAndAsset: (state, action) => {
            if (action.payload?.clientId) {
                state.selectedClientId = action.payload.clientId;
            }

            if (action.payload?.assetId) {
                state.selectedAssetId = action.payload.assetId;
            }
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchAppConfigValues.pending, (state) => {
                state.appLoadingStatus = LoadingStatus.Loading;
            })
            .addCase(fetchAppConfigValues.fulfilled, (state, action) => {
                state.appLoadingStatus = LoadingStatus.Loaded;
                state.appConfigValues = action.payload;
                state.error = null;
                state.appLoadedClientId = action.meta.arg.clientId;
                state.appLoadedAssetId = action.meta.arg.assetId;
            })
            .addCase(fetchAppConfigValues.rejected, (state, action) => {
                state.appLoadingStatus = LoadingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(fetchEditConfigValues.pending, (state) => {
                state.appLoadingStatus = LoadingStatus.Loading;
            })
            .addCase(fetchEditConfigValues.fulfilled, (state, action) => {
                state.editLoadingStatus = LoadingStatus.Loaded;
                state.editConfigValues = action.payload || [];
                state.error = null;
                state.editLoadedClientId = action.meta.arg.clientId;
                state.editLoadedAssetId = action.meta.arg.assetId;
            })
            .addCase(fetchEditConfigValues.rejected, (state, action) => {
                state.editLoadingStatus = LoadingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(updateConfigValues.pending, (state) => {
                state.savingStatus = SavingStatus.Saving;
            })
            .addCase(updateConfigValues.fulfilled, (state, action) => {
                state.savingStatus = SavingStatus.Saved;
                state.editLoadingStatus = LoadingStatus.Idle;
                state.editLoadedClientId = null;
                state.editLoadedAssetId = null;
                state.editConfigValues = [];
                state.error = null;
            })
            .addCase(updateConfigValues.rejected, (state, action) => {
                state.savingStatus = SavingStatus.Failed;
                state.error = action.error.message;
            });
    },
});

export const selectConfigValuesSelectedClientId = (state) => state.configValues.selectedClientId;
export const selectConfigValuesSelectedAssetId = (state) => state.configValues.selectedAssetId;
export const selectConfigValuesAppLoadedClientId = (state) => state.configValues.appLoadedClientId;
export const selectConfigValuesAppLoadedAssetId = (state) => state.configValues.appLoadedAssetId;
export const selectConfigValuesEditLoadedClientId = (state) => state.configValues.editLoadedClientId;
export const selectConfigValuesEditLoadedAssetId = (state) => state.configValues.editLoadedAssetId;
export const selectConfigValuesApp = (state) => state.configValues.appConfigValues;
export const selectConfigValuesEdit = (state) => state.configValues.editConfigValues;
export const selectConfigValuesErrorMessage = (state) => state.configValues.error;
export const selectConfigValuesSavingStatus = (state) => state.configValues.savingStatus;
export const selectConfigValuesAppLoadingStatus = (state) => state.configValues.appLoadingStatus;
export const selectConfigValuesEditLoadingStatus = (state) => state.configValues.editLoadingStatus;

const selectConfigValuesAppInternal = (state, clientId, assetId, configFields) => {
    return {
        clientId: clientId,
        assetId: assetId,
        configValues: state.configValues.appConfigValues,
        configFields: configFields,
    };
    /*
    // Simulate logic on server where all ConfigFields are shown - either
    // with empty value or value from ConfigValue

    return configFields.map((configField) => {
        // See if there is matching ConfigValue
        let configValue = state.configValues.configValues.find(
            (configValue) =>
                configValue.config_field_id === configField.config_field_id
        );

        if (!configValue) {
            configValue = createNewConfigValue();
            configValue.config_field_id = configField.config_field_id;
        }

        return {
            ...configValue,
            ...{
                display_name: configField?.display_name ?? '',
                category: configField?.category ?? '',
            },
        };
    });

    return state.configValues.configValues.map((configValue) => {
        const configField = configFields.find(
            (configField) =>
                configField.config_field_id === configValue.config_field_id
        );
        return {
            ...configValue,
            ...{
                display_name: configField?.display_name ?? '',
                category: configField?.category ?? '',
            },
        };
    });
        */
};

const selectConfigValuesEditInternal = (state, clientId, assetId, configFields) => {
    return {
        clientId: clientId,
        assetId: assetId,
        configValues: state.configValues.editConfigValues,
        configFields: configFields,
    };
    /*
    // Simulate logic on server where all ConfigFields are shown - either
    // with empty value or value from ConfigValue

    return configFields.map((configField) => {
        // See if there is matching ConfigValue
        let configValue = state.configValues.configValues.find(
            (configValue) =>
                configValue.config_field_id === configField.config_field_id
        );

        if (!configValue) {
            configValue = createNewConfigValue();
            configValue.config_field_id = configField.config_field_id;
        }

        return {
            ...configValue,
            ...{
                display_name: configField?.display_name ?? '',
                category: configField?.category ?? '',
            },
        };
    });

    return state.configValues.configValues.map((configValue) => {
        const configField = configFields.find(
            (configField) =>
                configField.config_field_id === configValue.config_field_id
        );
        return {
            ...configValue,
            ...{
                display_name: configField?.display_name ?? '',
                category: configField?.category ?? '',
            },
        };
    });
        */
};

// Have to use createSelector, otherwise returning a map of configValues
// directly results in an endless rerender loop. createSelector ensures
// that a cached result is returned if configValues and configFields have
// not changed

export const selectConfigValuesAppForDisplay = createSelector(
    selectConfigValuesAppInternal,
    ({ clientId, assetId, configValues, configFields }) => {
        // We need to show all ConfigFields, either
        // with empty value or value from ConfigValue

        return configFields?.map((configField) => {
            let configValue;
            let overrideValues = [];
            // See if there is matching ConfigValue that is not an override
            if (!clientId && !assetId && configValues) {
                configValue = configValues?.find(
                    (configValue) =>
                        configValue.config_field_id === configField.config_field_id &&
                        !configValue.client_id &&
                        !configValue.asset_id
                );
                overrideValues = configValues?.filter(
                    (configValue) =>
                        configValue.config_field_id === configField.config_field_id &&
                        (configValue.client_id || configValue.asset_id)
                );
            }
            if (clientId && !assetId && configValues) {
                configValue = configValues?.find(
                    (configValue) =>
                        configValue.config_field_id === configField.config_field_id && !configValue.asset_id
                );
                overrideValues = configValues?.filter(
                    (configValue) => configValue.config_field_id === configField.config_field_id && configValue.asset_id
                );
            }
            if (assetId && configValues) {
                configValue = configValues?.find(
                    (configValue) => configValue.config_field_id === configField.config_field_id
                );
            }

            if (!configValue) {
                configValue = createNewConfigValue();
                configValue.config_field_id = configField.config_field_id;
                configValue.field_source = 'Global';
                // Check for override values
            }

            const override_values = configValue.override_values?.concat(overrideValues) || [];

            // Add any override values

            return {
                ...configValue,
                override_values,
                ...{
                    display_name: configField?.display_name ?? '',
                    category: configField?.category ?? '',
                    dirty: false,
                },
            };
        });

        // return configValues.map((configValue) => {
        //     const configField = configFields.find(
        //         (configField) =>
        //             configField.config_field_id === configValue.config_field_id
        //     );
        //     return {
        //         ...configValue,
        //         ...{
        //             display_name: configField?.display_name ?? '',
        //             category: configField?.category ?? '',
        //         },
        //     };
        // });
    }
);

export const selectConfigValuesEditForDisplay = createSelector(
    selectConfigValuesEditInternal,
    ({ clientId, assetId, configValues, configFields }) => {
        // We need to show all ConfigFields, either
        // with empty value or value from ConfigValue

        return configFields?.map((configField) => {
            let configValue;
            let overrideValues = [];
            // See if there is matching ConfigValue that is not an override
            if (!clientId && !assetId && configValues) {
                configValue = configValues?.find(
                    (configValue) =>
                        configValue.config_field_id === configField.config_field_id &&
                        !configValue.client_id &&
                        !configValue.asset_id
                );
                overrideValues = configValues?.filter(
                    (configValue) =>
                        configValue.config_field_id === configField.config_field_id &&
                        (configValue.client_id || configValue.asset_id)
                );
            }
            if (clientId && !assetId && configValues) {
                configValue = configValues?.find(
                    (configValue) =>
                        configValue.config_field_id === configField.config_field_id && !configValue.asset_id
                );
                overrideValues = configValues
                    ?.filter(
                        (configValue) =>
                            configValue.config_field_id === configField.config_field_id && configValue.asset_id
                    )
                    .map((value) => {
                        var newValue = { ...value };
                        newValue.field_source = 'Asset';
                        return newValue;
                    });
            }
            if (assetId && configValues) {
                configValue = configValues?.find(
                    (configValue) => configValue.config_field_id === configField.config_field_id
                );
            }

            if (!configValue) {
                configValue = createNewConfigValue();
                configValue.config_field_id = configField.config_field_id;
                configValue.field_source = 'Global';
                if (clientId && !assetId) {
                    for (const overrideValue of overrideValues) {
                        if (overrideValue.global_values) {
                            configValue = { ...overrideValue.global_values };
                            configValue.field_source = 'Global';
                        }
                    }
                }
            }

            const override_values = configValue.override_values?.concat(overrideValues) || [];

            // Add any override values

            const result = {
                ...configValue,
                override_values,
                ...{
                    display_name: configField?.display_name ?? '',
                    category: configField?.category ?? '',
                    dirty: false,
                },
            };

            return result;
        });

        // return configValues.map((configValue) => {
        //     const configField = configFields.find(
        //         (configField) =>
        //             configField.config_field_id === configValue.config_field_id
        //     );
        //     return {
        //         ...configValue,
        //         ...{
        //             display_name: configField?.display_name ?? '',
        //             category: configField?.category ?? '',
        //         },
        //     };
        // });
    }
);

export const selectConfigValuesByFields = (configValues, configFields, keys = []) => {
    // We need to show all ConfigFields, either
    // with empty value or value from ConfigValue
    let selectedFields = [];
    configFields.forEach((configField) => {
        // See if there is matching ConfigValue
        let configValue = configValues.find(
            (configValue) => configValue.config_field_id === configField.config_field_id
        );

        if (configValue) {
            if (keys.includes(configField?.key)) {
                selectedFields[configField?.key] = configValue.value;
            }
        }
    });
    return selectedFields;
};

export const {
    clearAllConfigValues,
    clearAppConfigValues,
    clearEditConfigValues,
    setSelectedClientId,
    setSelectedAssetId,
    setSelectedClientAndAsset,
} = configValuesSlice.actions;

export default configValuesSlice.reducer;
